import React from "react";
import { useSelector } from "react-redux";
import {
  dealUsesFundraisingRounds,
  getDealCapacityMapSelector,
} from "@state/dealCapacity";
import {
  aggregateFundCapacity,
  LiveDealFundGroup,
  noticeRangeLabel,
} from "../fundGroups";
import { DealCardShell, DealCardStat } from "./DealCardShell";
import { FundraisingGauge } from "./FundraisingGauge";

const tenorChip = (group: LiveDealFundGroup): string =>
  group.tenors
    .map((tenor) =>
      tenor.noticeMonths != null
        ? `${tenor.noticeMonths}M`
        : (tenor.card.term ?? "")
    )
    .filter(Boolean)
    .join(" · ");

const statsFor = (group: LiveDealFundGroup): DealCardStat[] => {
  const representative = group.tenors[0].card;
  return [
    {
      label: "Target return",
      value: group.bestRateLabel ? `Up to ${group.bestRateLabel}` : undefined,
    },
    { label: "Risk", value: representative.risk },
    { label: "Liquidity", value: noticeRangeLabel(group.tenors) },
  ].filter((stat): stat is DealCardStat => Boolean(stat.value));
};

interface FundCardProps {
  group: LiveDealFundGroup;
  onSelect: (group: LiveDealFundGroup) => void;
}

export const FundCard = ({ group, onSelect }: FundCardProps): JSX.Element => {
  const capacityByDealId = useSelector(getDealCapacityMapSelector);

  const capacity = aggregateFundCapacity(
    group.tenors
      .filter((tenor) => dealUsesFundraisingRounds(tenor.card.deal))
      .map((tenor) => capacityByDealId[tenor.card.deal.id])
  );

  return (
    <DealCardShell
      name={group.name}
      tagline={noticeRangeLabel(group.tenors) ?? group.tenors[0].card.assetClass}
      icon={group.tenors[0].card.icon}
      investableType={group.tenors[0].card.investableType}
      termChip={tenorChip(group)}
      statusText={group.statusText}
      isaEligible={group.isaEligible}
      stats={statsFor(group)}
      onSelect={() => onSelect(group)}
      testID={`live-deals-v3-fund-${group.investableId}`}
    >
      {capacity ? (
        <FundraisingGauge
          capacity={capacity}
          currency={group.currency}
          testID={`fundraising-capacity-card-fund-${group.investableId}`}
        />
      ) : null}
    </DealCardShell>
  );
};
